import React, {useMemo, useState} from 'react';
import {CircularProgress} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

/**
 * Searchable, scrollable single-select table.
 *
 *
 * @param {Object} props
 * @param {Array<{key: string, label: string, subLabel: string}>} props.items - rows to choose from
 * @param {string} props.activeKey - row to mark with a check
 * @param {string} props.selecting - key of the row being selected, drives the row spinner
 * @param {Function} props.onSelect - called with the chosen row's key
 * @param {string} props.searchPlaceholder
 * @param {string} props.countNoun - plural noun used in the count line
 * @return {JSX.Element}
 */
function SearchableSelectTable(props) {
  const {
    items = [],
    activeKey = '',
    selecting = '',
    onSelect,
    searchPlaceholder = 'Search...',
    countNoun = 'items',
  } = props;
  const [searchText, setSearchText] = useState('');

  const visibleItems = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    if (!search) {
      return items;
    }
    // Match the secondary line too
    return items.filter((item) => {
      return (
        item.label.toLowerCase().includes(search) || (item.subLabel || '').toLowerCase().includes(search)
      );
    });
  }, [items, searchText]);

  return (
    <React.Fragment>
      <div className='form-group mb-2'>
        <input
          type='search'
          className='form-control'
          placeholder={searchPlaceholder}
          value={searchText}
          autoFocus
          onChange={(event) => {
            return setSearchText(event.target.value);
          }}
        />
      </div>

      <div style={{maxHeight: '45vh', overflowY: 'auto'}} className='border rounded'>
        <table className='table table-hover table-sm mb-0'>
          <tbody>
            {visibleItems.map((item) => {
              const isActive = item.key === activeKey;
              return (
                <tr
                  key={item.key}
                  onClick={() => {
                    if (!selecting) {
                      onSelect(item.key);
                    }
                  }}
                  style={{cursor: selecting ? 'default' : 'pointer'}}
                >
                  <td style={{width: '2rem'}}>{isActive && <FontAwesomeIcon icon='fas fa-check' />}</td>
                  <td>
                    <div>{item.label}</div>
                    {item.subLabel && item.subLabel !== item.label && (
                      <small className='text-muted'>{item.subLabel}</small>
                    )}
                  </td>
                  <td style={{width: '2rem'}}>{selecting === item.key && <CircularProgress size={16} />}</td>
                </tr>
              );
            })}
            {visibleItems.length === 0 && (
              <tr>
                <td colSpan={3} className='text-center text-muted py-3'>
                  No {countNoun} match &quot;{searchText}&quot;
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <small className='text-muted d-block mt-2'>
        {visibleItems.length} of {items.length} {countNoun}
      </small>
    </React.Fragment>
  );
}

export {SearchableSelectTable};
